import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AudioRecorder.module.css";

interface AudioRecorderProps {
    onRecordingComplete: (audioFile: File) => void;
    onClose: () => void;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onRecordingComplete, onClose }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [error, setError] = useState<string | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                mediaRecorderRef.current.stop();
            }
        };
    }, []);

    const startRecording = async () => {
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: "audio/webm;codecs=opus",
            });

            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                setAudioBlob(audioBlob);
                stream.getTracks().forEach((track) => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);

            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } catch (err) {
            console.error("Error accessing microphone:", err);
            setError(t("microphoneAccessDenied") || "Could not access microphone");
        }
    };

    const pauseRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.pause();
            setIsPaused(true);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    };

    const resumeRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
            mediaRecorderRef.current.resume();
            setIsPaused(false);
            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    };

    const handleUseRecording = () => {
        if (audioBlob) {
            // Convert Blob to File
            const audioFile = new File([audioBlob], `recording_${Date.now()}.webm`, {
                type: "audio/webm",
            });
            onRecordingComplete(audioFile);
        }
    };

    const handleRestart = () => {
        setAudioBlob(null);
        setRecordingTime(0);
        audioChunksRef.current = [];
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.closeButton} onClick={onClose}>
                    ×
                </button>

                <h2 className={styles.modalTitle}>{t("recordAudio")}</h2>

                {error && <div className={styles.errorMessage}>{error}</div>}

                <div className={styles.recorderContainer}>
                    {!audioBlob ? (
                        <>
                            <div className={styles.visualizer}>
                                <div className={`${styles.micIcon} ${isRecording ? styles.recording : ""}`}>
                                    <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <div className={styles.timer}>{formatTime(recordingTime)}</div>

                            <div className={styles.controls}>
                                {!isRecording ? (
                                    <button className={styles.startButton} onClick={startRecording}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="8" />
                                        </svg>
                                        {t("startRecording")}
                                    </button>
                                ) : (
                                    <>
                                        {!isPaused ? (
                                            <button className={styles.pauseButton} onClick={pauseRecording}>
                                                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                    <rect x="6" y="4" width="4" height="16" />
                                                    <rect x="14" y="4" width="4" height="16" />
                                                </svg>
                                                {t("pause")}
                                            </button>
                                        ) : (
                                            <button className={styles.resumeButton} onClick={resumeRecording}>
                                                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                                {t("resume")}
                                            </button>
                                        )}
                                        <button className={styles.stopButton} onClick={stopRecording}>
                                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                <rect x="6" y="6" width="12" height="12" />
                                            </svg>
                                            {t("stop")}
                                        </button>
                                    </>
                                )}
                            </div>

                            {recordingTime < 10 && isRecording && (
                                <p className={styles.minimumWarning}>
                                    {t("minimumRecordingTime")}: {10 - recordingTime}s
                                </p>
                            )}
                        </>
                    ) : (
                        <>
                            <div className={styles.playbackContainer}>
                                <div className={styles.successIcon}>✅</div>
                                <p className={styles.successMessage}>{t("recordingComplete")}</p>
                                <audio controls src={URL.createObjectURL(audioBlob)} className={styles.audioPlayer} />
                                <p className={styles.recordingDuration}>
                                    {t("duration")}: {formatTime(recordingTime)}
                                </p>
                            </div>

                            <div className={styles.playbackControls}>
                                <button className={styles.restartButton} onClick={handleRestart}>
                                    {t("recordAgain")}
                                </button>
                                <button
                                    className={styles.useButton}
                                    onClick={handleUseRecording}
                                    disabled={recordingTime < 10}
                                >
                                    {t("useRecording")}
                                </button>
                            </div>

                            {recordingTime < 10 && (
                                <p className={styles.warningMessage}>
                                    ⚠️ {t("recordingTooShort")} ({recordingTime}s / 10s minimum)
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AudioRecorder;
